import HolisticsLinkIcon from '@assets/icons/holistics-link.svg';
import React from 'react';
import StaticLinkIcon from '@assets/icons/static-link.svg';
import { ExternalContentFragment } from '@/types';
import { getDisplayedNameText } from '@/utils/string';
import { isExternalContentStaticLink } from '@/contexts/external-content-context';
import { RemoteIcon } from '@/experiences/shared/components/remote-icon';
import { theme } from '@/styles';

interface ExternalContentLinkIconProps {
  iconSize?: number;
  link: ExternalContentFragment;
}

/**
 * Determines the appropriate icon for the given external content link.
 *
 * @param link - The external content link to determine the icon for.
 * @param iconSize - The size of the icon to be displayed (default is 24).
 * @returns A JSX element representing the icon for the external content link.
 */
export const ExternalContentLinkIcon = React.memo(
  ({ link, iconSize = 24 }: ExternalContentLinkIconProps) => {
    const linkUrl = link?.config?.icon_url;
    const displayName = getDisplayedNameText(link.display_name, link.name);

    if (linkUrl) {
      // RemoteIcon: SVG urls render through SvgUri on native (RN Image
      // silently drops SVG); plain Image elsewhere.
      return <RemoteIcon alt={displayName} size={iconSize} uri={linkUrl} />;
    }

    const Icon = isExternalContentStaticLink(link)
      ? StaticLinkIcon
      : HolisticsLinkIcon;
    return (
      <Icon height={iconSize} stroke={theme.colors.primary} width={iconSize} />
    );
  },
);
